import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as UploadIcon } from '../../assets/svg/upload_icon.svg'
import Button from '../../elements/button'
import Text from '../../elements/Typo/Text'
import Icon from '../icons'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import { useLargeUploadApi } from '../../store/api/hooks/largeUploadApiHook'
import { ErrorResponse, SuccessApiResponse } from '../../store/api/apiTypes'
import UploadSucessModal from './uploadSucess.modal'
import { UploadStatusType } from '../../_config/types'
import Loader from '../loader'
import { useParams } from 'react-router-dom'
import { useExperimentSelector } from '../../store/selectors/experimentSelector'
import { useDialog } from '../../store/api/hooks/dialogHook'
import toast from 'react-hot-toast'
import { trackEvent } from '../../_common/functions'
import { useMeasurementConfigure } from '../../_common/hook/useMeasurementConfigure'
// import Dropdown from '../../elements/Form/dropdown'

import TypingEffect from '../typingEffect'
import moment from 'moment'
import pusherService from '../../services/pusherService'
import RecurringFileUploadErrorModal from './recurringFileUploadError.modal'
import CsvImportDialog from '../../features/csv/components/CsvImportDialog'
import { CsvColumnConfig } from '../../features/csv/types/csv.types'

interface Props {
    uploadType: 'intervention' | 'measurement' | 'design' | 'recommendation' | 'recurring_measurement'
    literal: string
    uploadingStatus: (data: UploadStatusType) => void
    isFileChanged?: (status: boolean) => void
    recurringSelection?: boolean
    onFileUploadSuccess?: (data: any) => void
    forChilds?: any
    experimentData?: any
    csvImportMode?: 'off' | 'profile-then-upload'
    onCsvImportConfirm?: (payload: { fileName: string; totalRows: number; delimiter: string; configs: CsvColumnConfig[] }) => void
    // disabled?: boolean
    // onUpload?: (file: File) => void
    // onRetry?: () => void
    // onFileClear?: () => void
    // uploadStatus?: UploadStatusType
    // uploadProgress?: number
    // showUploadProgress?: boolean
    // acceptedFileTypes?: string[]
    // maxFileSize?: number
}

const DataFileUploader: React.FC<Props> = ({ uploadType, literal, uploadingStatus, isFileChanged = (status) => {}, recurringSelection, onFileUploadSuccess, forChilds, experimentData, csvImportMode = 'profile-then-upload', onCsvImportConfirm }) => {
    const { t } = useTranslation()
    const params = useParams()
    const reduxExperiment = useExperimentSelector()
    const experiment = experimentData ?? reduxExperiment
    const experimentApi: any = useExperimentApi()
    const largeUploadApi = useLargeUploadApi()
    const dialog = useDialog()
    const selectedFileNameRef = useRef<string | null>(null)
    const [uploadedDataRef, setUploadedDataRef] = useState<any>(null)
    const recurringFileUploadResponseRef = useRef<any>(null)
    const [progress, setProgress] = useState<number>(0)
    const [totalFileSize, setTotalFileSize] = useState<number>(0)
    const [uploadedBytes, setUploadedBytes] = useState<number>(0)
    const [uploadSpeed, setUploadSpeed] = useState<number>(0)
    const [workingStatus, setWorkingStatus] = useState<UploadStatusType>('ready-for-upload')
    const uploadsucessModalRef: any = useRef()
    const recurringFileUploadErrorModalRef: any = useRef()
    const measurementHookFunction = useMeasurementConfigure({ experimentType: 'RCT' })

    const [uploadFor, setUploadFor] = useState<any[] | null>(null)
    // const [uploadForChild, setUploadForChild] = useState<any>(null)
    const uploadForChildRef = useRef<any>(null)
    // const [uploadForChildError, setUploadForChildError] = useState<string>('')

    const [qualityStatus, setQualityStatus] = useState<string>('Quality check started')
    const [processingStatus, setProcessingStatus] = useState<string>('Processing approved upload started')
    const [recurringFileUploadError, setRecurringFileUploadError] = useState<any>(null)
    const [csvImportFile, setCsvImportFile] = useState<File | null>(null)

    const formatBytes = (bytes: number): string => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }

    // RFC 4180-ish parser for a single header line (handles quoted fields and escaped quotes).
    const parseCsvHeaderLine = (line: string): string[] => {
        const result: string[] = []
        let current = ''
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
            const ch = line[i]
            if (inQuotes) {
                if (ch === '"' && line[i + 1] === '"') {
                    current += '"'
                    i++
                } else if (ch === '"') {
                    inQuotes = false
                } else {
                    current += ch
                }
            } else if (ch === '"') {
                inQuotes = true
            } else if (ch === ',') {
                result.push(current.trim())
                current = ''
            } else {
                current += ch
            }
        }
        result.push(current.trim())
        return result
    }

    const readCsvColumnNames = async (file: File): Promise<string[]> => {
        const READ_CHUNK = 64 * 1024
        let text = ''
        let offset = 0
        let headerLine = ''
        while (offset < file.size) {
            const slice = file.slice(offset, Math.min(offset + READ_CHUNK, file.size))
            text += await slice.text()
            const match = /\r?\n/.exec(text)
            if (match) {
                headerLine = text.slice(0, match.index)
                break
            }
            offset += READ_CHUNK
        }
        if (!headerLine) headerLine = text
        if (headerLine.startsWith('\uFEFF')) headerLine = headerLine.slice(1)
        return parseCsvHeaderLine(headerLine).filter((c) => c.length > 0)
    }

    useEffect(() => {
        if (Array.isArray(forChilds) && forChilds.length > 0) {
            const rchilds: any = forChilds.filter((child: any) => child.status == 100 || child.status == 101 || child.status == 'Scheduled').sort((a: any, b: any) => moment(a.raw.end_date, 'YYYY-MM-DD').diff(moment(b.raw.end_date, 'YYYY-MM-DD')))
            // console.log('forChilds in data file uploader', rchilds, rchilds[0]);
            uploadForChildRef.current =
                rchilds?.length >= 1
                    ? {
                          label: rchilds[0].expName,
                          value: rchilds[0].id,
                      }
                    : null
            setUploadFor(rchilds?.length >= 1 ? rchilds : null)
        } else {
            setUploadFor(null)
        }
    }, [forChilds])

    const mappingKeys: { [key: string]: string } = {
        design: 'design_data_quality_report',
        measurement: 'measurement_data_quality_report',
        intervention: 'intervention_data_quality_report',
        recommendation: 'recommendation_data_quality_report',
    }

    const tableKeys: { [key: string]: string } = {
        design: 'user_created_table_name',
        measurement: 'user_created_measurement_table_name',
        intervention: 'user_created_intervention_table_name',
        recommendation: 'user_created_recommendation_table_name',
    }

    const uploadIDRef = useRef<string | number | null>(null)

    const CHUNK_SIZE = 16 * 1024 * 1024 // 16MB

    // Chunks are uploaded as bytes, but keep UTF-8 code points together so an
    // intermediary that inspects a chunk as text never sees a partial character.
    const getChunkOffsets = async (file: File): Promise<number[]> => {
        const boundaries: number[] = []
        for (let boundary = CHUNK_SIZE; boundary < file.size; boundary += CHUNK_SIZE) {
            boundaries.push(boundary)
        }
        const adjustedBoundaries = await Promise.all(
            boundaries.map(async (boundary) => {
                const boundaryBytes = new Uint8Array(await file.slice(boundary, Math.min(boundary + 3, file.size)).arrayBuffer())
                let adjustedBoundary = boundary
                while (adjustedBoundary - boundary < boundaryBytes.length && (boundaryBytes[adjustedBoundary - boundary] & 0xc0) === 0x80) {
                    adjustedBoundary++
                }
                return adjustedBoundary
            }),
        )
        return [0, ...adjustedBoundaries, file.size]
    }

    const compressChunk = async (chunk: Blob): Promise<Blob> => {
        if (typeof CompressionStream !== 'undefined') {
            const cs = new CompressionStream('gzip')
            const stream = chunk.stream().pipeThrough(cs)
            const compressedResponse = new Response(stream)
            return await compressedResponse.blob()
        }
        return chunk
    }

    const MAX_RETRIES = 3

    const waitForOnline = (): Promise<void> => {
        if (navigator.onLine) return Promise.resolve()
        return new Promise((resolve) => {
            const handler = () => {
                window.removeEventListener('online', handler)
                resolve()
            }
            window.addEventListener('online', handler)
        })
    }

    const uploadSingleChunk = (params: { file: File; uploadId: string; chunkIndex: number; totalChunks: number; chunkOffsets: number[]; isGzipSupported: boolean }, onDone: () => void, onFail: (error: any) => void, retryCount = 0) => {
        const { file, uploadId, chunkIndex, totalChunks, chunkOffsets, isGzipSupported } = params
        const start = chunkOffsets[chunkIndex]
        const end = chunkOffsets[chunkIndex + 1]
        const rawChunk = file.slice(start, end)

        const handleChunkError = (error: any) => {
            if (retryCount < MAX_RETRIES) {
                waitForOnline()
                    .then(() => {
                        uploadSingleChunk(params, onDone, onFail, retryCount + 1)
                    })
                    .catch((error) => {
                        console.error('Error :', error)
                    })
            } else {
                onFail(error)
            }
        }

        const sendChunk = (chunkBlob: Blob) => {
            const formData = new FormData()
            formData.append('upload_id', uploadId)
            formData.append('chunk_index', String(chunkIndex))
            formData.append('is_gzip_compressed', String(isGzipSupported))
            formData.append('file', chunkBlob, `chunk_${chunkIndex}`)

            largeUploadApi.uploadChunk(
                {
                    formData,
                    progress: (progressEvent: any) => {
                        const chunkProgress = progressEvent.progress || 0
                        const overallProgress = ((chunkIndex + chunkProgress) / totalChunks) * 100
                        setProgress(Math.round(overallProgress))
                        const chunkSize = end - start
                        const bytesInChunk = chunkProgress * chunkSize
                        const currentBytes = start + bytesInChunk
                        setUploadedBytes(currentBytes)
                        if (progressEvent.rate) {
                            setUploadSpeed(progressEvent.rate)
                        }
                    },
                },
                () => onDone(),
                (error: any) => handleChunkError(error),
            )
        }

        if (isGzipSupported) {
            compressChunk(rawChunk)
                .then(sendChunk)
                .catch(() => onFail({ message: 'Failed to compress file chunk' }))
        } else {
            sendChunk(rawChunk)
        }
    }

    const onChunkUploadError = (error: any) => {
        if (error?.reserved_columns) {
            dialog.confirm({
                type: 'delete',
                title: 'Column Name Conflicts Detected',
                message: `The uploaded dataset contains column names that conflict with reserved TestOps fields`,
                warning: `Please rename <b>${error?.reserved_columns?.join(', ')}</b> columns and re-upload your dataset to proceed.`,
                confirmButtonLabel: 'Re-Upload Updated Data',
                cancelButton: false,
                onConfirm: () => {
                    setWorkingStatus('ready-for-upload')
                },
            })
        } else {
            setWorkingStatus('ready-for-upload')
            toast.error(error?.message || `Failed to upload chunk`)
        }
    }

    const uploadChunksSequentially = (file: File, uploadId: string, chunkOffsets: number[], isGzipSupported: boolean) => {
        const totalChunks = chunkOffsets.length - 1
        let uploadedChunks = 0
        let lastTime = Date.now()
        let lastBytes = 0

        const uploadNext = (chunkIndex: number) => {
            if (chunkIndex >= totalChunks) {
                completeUploadSession(uploadId)
                return
            }

            uploadSingleChunk(
                { file, uploadId, chunkIndex, totalChunks, chunkOffsets, isGzipSupported },
                () => {
                    uploadedChunks++
                    const bytesNow = chunkOffsets[uploadedChunks]
                    const now = Date.now()
                    const elapsed = (now - lastTime) / 1000
                    if (elapsed > 0) {
                        setUploadSpeed((bytesNow - lastBytes) / elapsed)
                    }
                    lastTime = now
                    lastBytes = bytesNow
                    setUploadedBytes(bytesNow)
                    setProgress(Math.round((bytesNow / file.size) * 100))
                    uploadNext(chunkIndex + 1)
                },
                onChunkUploadError,
            )
        }

        uploadNext(0)
    }

    const completeUploadSession = (uploadId: string) => {
        largeUploadApi.completeLargeUpload(
            { upload_id: uploadId },
            () => {},
            (error: any) => {
                setWorkingStatus('ready-for-upload')
                toast.error(error?.message || 'Failed to complete upload')
            },
        )
    }

    const pollUploadStatus = (uploadId: string) => {
        largeUploadApi.getLargeUploadStatus(
            { upload_id: uploadId },
            (statusResp: any) => {
                const status = statusResp?.data?.status
                const qualityReport = statusResp?.data?.quality_report
                if ((status === 'READY_FOR_APPROVAL' || status === 'COMPLETED') && qualityReport) {
                    const response = { design_data_quality_report: qualityReport }
                    recurringFileUploadResponseRef.current = response
                    setWorkingStatus('data-uploaded')
                    setUploadedDataRef(qualityReport)
                    uploadsucessModalRef?.current?.showModal()
                } else if (status === 'FAILED' || status === 'REJECTED') {
                    setWorkingStatus('ready-for-upload')
                    toast.error(statusResp?.data?.error_detail || 'Upload processing failed')
                } else {
                    setTimeout(() => pollUploadStatus(uploadId), 3000)
                }
            },
            (error: any) => {
                setWorkingStatus('ready-for-upload')
                toast.error(error?.message || 'Failed to check upload status')
            },
        )
    }

    const uploadfiletoserver = async (event: any, configs?: CsvColumnConfig[]) => {
        const file = event.target.files?.[0]

        if (!file) {
            console.warn('No file selected')
            return
        }

        // check file name first character is not $ or _
        if (file.name.startsWith('$') || file.name.startsWith('_')) {
            toast.error(t('File name cannot start with $ or _'))
            event.target.value = ''
            return
        }

        selectedFileNameRef.current = file.name
        setProgress(0)
        setTotalFileSize(file.size)
        setUploadedBytes(0)
        setUploadSpeed(0)
        setWorkingStatus('uploading')

        const chunkOffsets = await getChunkOffsets(file)
        const totalChunks = chunkOffsets.length - 1
        const isGzipSupported = typeof CompressionStream !== 'undefined'

        const columnNames = await readCsvColumnNames(file).catch((err: any) => {
            console.error('Failed to read CSV headers', err)
            toast.error(t('Failed to read CSV headers'))
            return null
        })
        if (columnNames === null) {
            setWorkingStatus('ready-for-upload')
            return
        }

        const payload: any = {
            experiment_literal: literal,
            upload_type: uploadType,
            file_name: file.name,
            total_chunks: totalChunks,
            file_size_bytes: file.size,
            csv_column_names: columnNames,
        }

        if (configs) {
            payload.is_advance_option = true
            payload.column_configs = configs.map((config: CsvColumnConfig) => {
                const columnName = config.outputName ?? config.name
                if (config.selectedType === 'string' || config.selectedType === 'boolean') {
                    return {
                        column_name: columnName,
                        data_type: 'text',
                        trim_text: true,
                        empty_to_null: true,
                    }
                }
                if (config.selectedType === 'integer') {
                    return {
                        column_name: columnName,
                        data_type: 'integer',
                        thousands_separator: config.thousandsSeparator,
                    }
                }
                if (config.selectedType === 'float') {
                    return {
                        column_name: columnName,
                        data_type: 'decimal',
                        decimal_separator: config.decimalSeparator,
                        thousands_separator: config.thousandsSeparator,
                    }
                }
                if (config.selectedType === 'date' || config.selectedType === 'datetime') {
                    return {
                        column_name: columnName,
                        data_type: 'date',
                        date_format: config.dateFormat,
                    }
                }
            })

            console.log('Payload -------------------', payload)

            /* [
                {
                    "column_name": "order_date",
                    "data_type": "date",
                    "date_format": "dd/MM/yyyy"
                },
                {
                    "column_name": "amount_dot",
                    "data_type": "decimal",
                    "decimal_separator": ".",
                    "thousands_separator": ""
                },
                {
                    "column_name": "amount_comma",
                    "data_type": "decimal",
                    "decimal_separator": ",",
                    "thousands_separator": ""
                },
                {
                    "column_name": "id",
                    "data_type": "integer",
                    "thousands_separator": ""
                },
                {
                    "column_name": "category",
                    "data_type": "text",
                    "trim_text": true,
                    "empty_to_null": true
                }
            ] */
        }

        largeUploadApi.initLargeUpload(
            payload,
            (initResp: any) => {
                const uploadId = initResp?.data?.upload_id
                uploadIDRef.current = uploadId
                if (!uploadId) {
                    setWorkingStatus('ready-for-upload')
                    toast.error('Failed to initialise upload session')
                    return
                }
                uploadChunksSequentially(file, uploadId, chunkOffsets, isGzipSupported)
            },
            (error: any) => {
                setWorkingStatus('ready-for-upload')
                const msg: string = error?.message || 'Failed to start upload'
                if (error.error?.existing_columns) {
                    setRecurringFileUploadError(error.error)
                } else {
                    toast.error(msg)
                }
            },
        )
    }

    const startUpload = (event: any, configs?: CsvColumnConfig[]) => {
        uploadfiletoserver(event, configs).catch((error: any) => {
            console.error('Failed to start upload', error)
            setWorkingStatus('ready-for-upload')
            toast.error(error?.message || 'Failed to start upload')
        })
    }

    const continueToStep = () => {
        trackEvent('show_recommendation', {
            uploadType,
        })

        largeUploadApi.processLargeUpload(
            { upload_id: uploadIDRef.current as string },
            (data: SuccessApiResponse<[]>) => {
                setWorkingStatus('data-mapping')
                if (experiment?.step > 1 && !recurringSelection) {
                    isFileChanged(true)
                }
                uploadsucessModalRef?.current?.close()
            },
            (error: ErrorResponse<[]>) => {
                uploadsucessModalRef?.current?.close()
            },
        )
    }

    const getExperimentDetails = () => {
        experimentApi.getExperimentDetails(
            { literal, use_orm: true },
            (resp: any) => {},
            (error: any) => {
                console.log('worker error --', error)
            },
        )
    }

    const RecurringUploadWatch = (n: number) => {
        console.log('Run - RecurringUploadWatch')
        experimentApi.recurringFileProcessWatch(
            { literal, n },
            (resp: any) => {
                const newFileName = resp?.data?.user_created_measurement_table_name
                if (newFileName) {
                    setWorkingStatus('data-ready')
                    if (onFileUploadSuccess) {
                        const data: any = {
                            tableName: newFileName,
                            data_quality_report: recurringFileUploadResponseRef.current?.design_data_quality_report,
                        }
                        if (uploadForChildRef.current) {
                            data.apply_on_exp_literal = uploadForChildRef.current.value
                        }
                        onFileUploadSuccess(data)
                    }
                } else {
                    setTimeout(() => {
                        RecurringUploadWatch(n + 1)
                    }, 3000)
                }
            },
            (error: any) => {
                console.log('worker error --', error)
            },
        )
    }

    const checkDataMappingWatch = (n: number) => {
        experimentApi.getExperimentDetailsWatch(
            { literal, use_orm: true, n },
            (resp: any) => {
                const key = mappingKeys[uploadType]
                const newFileName = resp?.experiment_metadatas?.[key]?.file_name
                const currentFileName = selectedFileNameRef.current
                console.log('checkDataMappingWatch', newFileName, currentFileName, currentFileName, n)
                if (newFileName == currentFileName && newFileName) {
                    if (resp?.data_mappings?.[tableKeys[uploadType]]) {
                        setWorkingStatus('data-ready')
                        getExperimentDetails()
                    } else {
                        setTimeout(() => {
                            checkDataMappingWatch(n + 1)
                        }, 3000)
                    }
                } else {
                    setTimeout(() => {
                        checkDataMappingWatch(n + 1)
                    }, 3000)
                }
            },
            (error: any) => {
                console.log('worker error --', error)
            },
        )
    }

    const downloadTemplate = () => {
        trackEvent('measurement_template_downloaded', { experiment_literal: literal, upload_type: uploadType })
        const conversionMetric: any = experiment?.experiment_metadatas?.metrices.filter((metric: any) => metric.literral_value === 'Incremental Conversion') ?? []

        const expType = experiment?.experiment_type?.title
        const isMeasurementFlow = experiment?.is_measurement_flow
        const open = (fileUrl: string) => window.open(fileUrl, '_blank')
        const masterCopy = true

        //console.log('experiment.experiment_type.title: ', expType, ' uploadType: ', uploadType)
        //TestOps_Upload_Template_with_Explanatory_Guide.xlsx

        const handleSpecialCases = (): boolean => {
            if (masterCopy) {
                open('/assets/TestOps_Upload_Template_with_Explanatory_Guide.xlsx')
                return true
            }
            if (isMeasurementFlow && expType === 'RCT' && uploadType === 'measurement') {
                open('/assets/Sample_Data_RCT_Measurement_only.csv')
                return true
            }
            if (expType === 'RCT' && uploadType === 'measurement') {
                open('/assets/Sample_Data_Measurement_RCT.csv')
                return true
            }
            if (expType === 'RCT' && uploadType === 'design') {
                open('/assets/Sample_Data_RCT_Design.csv')
                return true
            }
            if (isMeasurementFlow && expType === 'Observational Study' && uploadType === 'measurement') {
                open('/assets/Sample_Data_Obs_Measure_Only.csv')
                return true
            }
            if (expType === 'Observational Study' && uploadType === 'measurement') {
                open('/assets/Sample_Data_Obs_Measurement.csv')
                return true
            }
            if (expType === 'Observational Study' && uploadType === 'design') {
                open('/assets/Sample_Data_Ob_Design.csv')
                return true
            }
            return false
        }

        if (handleSpecialCases()) {
            return
        }

        if (uploadType === 'design') {
            open('/assets/Experiment_Design_Template_data.xlsx')
        } else if (uploadType === 'measurement' || uploadType === 'intervention') {
            open('/assets/Measurement_Sample_Data.xlsx')
        }

        if (conversionMetric.length > 0) {
            if (uploadType === 'measurement') open('/assets/measurement_ten_percent(in).csv')
            if (uploadType === 'recommendation') open('/assets/recommendation_ten_percent(in).csv')
        }
    }

    const upload_quality_update = (data: any) => {
        if (uploadIDRef.current == data.upload_id) {
            setQualityStatus(data.message)
        }
    }

    const upload_quality_ready = (data: any) => {
        if (uploadIDRef.current == data.upload_id) {
            setQualityStatus(data.message)
            pollUploadStatus(data.upload_id)
        }
    }

    const upload_quality_failed = (data: any) => {
        if (uploadIDRef.current == data.upload_id) {
            setQualityStatus(data.message || data.error)
            toast.error(data.message || data.error)
            setWorkingStatus('ready-for-upload')
        }
    }

    const upload_processing_update = (data: any) => {
        if (uploadIDRef.current == data.upload_id) {
            setProcessingStatus(data.message)
        }
    }

    const upload_processing_complete = (data: any) => {
        if (uploadIDRef.current == data.upload_id) {
            setProcessingStatus(data.message)
            if (recurringSelection) {
                RecurringUploadWatch(1)
            } else {
                checkDataMappingWatch(1)
            }
        }
    }

    const upload_processing_failed = (data: any) => {
        if (uploadIDRef.current == data.upload_id) {
            setProcessingStatus(data.message || data.error)
            toast.error(data.message || data.error)
            setWorkingStatus('ready-for-upload')
        }
    }

    const addPusherEvents = () => {
        pusherService.bind('upload_quality_update', upload_quality_update)
        pusherService.bind('upload_quality_ready', upload_quality_ready)
        pusherService.bind('upload_quality_failed', upload_quality_failed)
        pusherService.bind('upload_processing_update', upload_processing_update)
        pusherService.bind('upload_processing_complete', upload_processing_complete)
        pusherService.bind('upload_processing_failed', upload_processing_failed)

        return () => {
            pusherService.unbind('upload_quality_update', upload_quality_update)
            pusherService.unbind('upload_quality_ready', upload_quality_ready)
            pusherService.unbind('upload_quality_failed', upload_quality_failed)
            pusherService.unbind('upload_processing_update', upload_processing_update)
            pusherService.unbind('upload_processing_complete', upload_processing_complete)
            pusherService.unbind('upload_processing_failed', upload_processing_failed)
        }
    }

    useEffect(() => {
        uploadingStatus(workingStatus)
        if (workingStatus == 'data-ready' && params.experiment == literal) {
            experimentApi.getExperimentDetails(
                { literal: params.experiment + '?use_orm=true' },
                (resp: any) => {},
                (error: any) => {},
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workingStatus])

    useEffect(() => {
        const mapping = {
            design: {
                table: experiment?.data_mappings?.user_created_table_name,
                report: experiment?.experiment_metadatas?.design_data_quality_report,
            },
            measurement: {
                table: experiment?.data_mappings?.user_created_measurement_table_name,
                report: experiment?.experiment_metadatas?.measurement_data_quality_report,
            },
            intervention: {
                table: experiment?.data_mappings?.user_created_intervention_table_name,
                report: experiment?.experiment_metadatas?.intervention_data_quality_report,
            },
            recommendation: {
                table: experiment?.data_mappings?.user_created_recommendation_table_name,
                report: experiment?.experiment_metadatas?.recommendation_data_quality_report,
            },
            recurring_measurement: {
                table: undefined,
                report: undefined,
            },
        }

        const current = mapping[uploadType]
        if (current?.table && current?.report && !recurringSelection) {
            selectedFileNameRef.current = current.report.file_name
            setUploadedDataRef(current.report)
            setWorkingStatus('data-ready')
        }
    }, [experiment, uploadType])

    useEffect(() => {
        addPusherEvents()
    }, [])

    return (
        <div className="p-4 flex flex-col items-center justify-center h-[18.167rem] flex-grow rounded-[0.417rem] border border-dashed border-[##BEBEBE]">
            {workingStatus === 'data-mapping' && (
                <div>
                    <Loader />
                    <Text size="lg" className="text-center font-semibold">
                        {t('Mapping Data in progress')}
                    </Text>
                    {processingStatus && (
                        <Text size="15" className="text-center italic text-slate-600 mt-2">
                            <TypingEffect text={processingStatus} />
                        </Text>
                    )}
                    <Text size="lg" className="text-center ">
                        {t('Please wait')}
                    </Text>
                </div>
            )}
            {workingStatus === 'data-ready' && (
                <div className="flex flex-col items-center justify-center">
                    <Icon name="cloud-check" size={4} className="text-green-700" />

                    <Text size="md" className="text-center font-semibold mt-2 !text-slate-500">
                        {t('Data Uploaded Successfully')}
                    </Text>

                    <Button
                        className="mt-4"
                        size="sm"
                        theme="tertiary"
                        onClick={() => {
                            if (uploadedDataRef) {
                                trackEvent('measurement_data_quality_report_reopened', { experiment_literal: literal, upload_type: uploadType })
                                uploadsucessModalRef?.current?.showModal()
                            }
                        }}
                    >
                        <Icon name="eye" />
                        {t('View Data Quality Report')}
                    </Button>
                </div>
            )}

            {workingStatus === 'uploading' && (
                <div className="flex flex-col items-center justify-center" data-testid="upload-progress">
                    <Icon name="cloud-upload" size={4} className="animate-[pulse_1s_ease-in-out_infinite] text-gray-700" />
                    {progress < 100 ? (
                        <>
                            <div className="w-[12rem] h-[0.25rem] bg-slate-200 rounded-md overflow-hidden">
                                <div className="h-full bg-primary rounded-md transition-all" style={{ width: `${progress}%` }}></div>
                            </div>
                            <Text size="md" className="text-center font-semibold mt-2 !text-slate-500">
                                {progress}%
                            </Text>
                            <Text size="sm" className="text-center mt-1 !text-slate-400">
                                {formatBytes(uploadedBytes)} / {formatBytes(totalFileSize)}
                                {uploadSpeed > 0 && ` • ${formatBytes(uploadSpeed)}/s`}
                            </Text>
                        </>
                    ) : (
                        <div className="flex justify-center flex-col">
                            <Text size="lg" className="text-center font-semibold">
                                {t('Generating data quality report')}
                            </Text>
                            <Text size="sm" className="text-center mt-1 !text-slate-400">
                                <TypingEffect text={qualityStatus} />
                            </Text>
                            <Text size="lg" className="text-center font-semibold">
                                {t('Please wait')}
                            </Text>
                        </div>
                    )}
                </div>
            )}

            {workingStatus !== 'data-mapping' && workingStatus !== 'data-ready' && workingStatus !== 'uploading' && experiment && (
                <>
                    <UploadIcon />
                    <Text size="xl" className="text-[#676767] pb-3">
                        {t('Supported formats: CSV')}
                    </Text>
                    <Button data-testid="upload-file" className="!font-medium w-full max-w-[78%] m-[0.773rem] relative !px-0">
                        {recurringSelection && uploadFor && !uploadForChildRef.current && (
                            <button
                                type="button"
                                className="bg-transparent opacity-50 w-full h-full absolute top-0 left-0 z-10"
                                onClick={(e: any) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    // setUploadForChildError(t('Please select recurring child'))
                                    uploadForChildRef.current = null
                                }}
                            ></button>
                        )}
                        {t('Upload Files')} <Icon name="upload" />
                        <input
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file && !file.name.toLowerCase().endsWith('.csv')) {
                                    e.target.value = ''
                                    toast.error(t('Only CSV files are accepted'))
                                    return
                                }

                                trackEvent('Design_UploadFile_Click', {
                                    timestamp: new Date().toISOString(),
                                    file_type: e.target.files?.[0]?.type || 'unknown',
                                    step: experiment?.step,
                                })
                                trackEvent('measurement_file_upload_started', {
                                    experiment_literal: literal,
                                    upload_type: uploadType,
                                })

                                if (file && csvImportMode === 'profile-then-upload') {
                                    setCsvImportFile(file)
                                    e.target.value = ''
                                    return
                                }

                                startUpload(e)
                            }}
                            data-testid="file-input"
                            onMouseDown={(e: any) => {
                                if (uploadType == 'design' && !recurringSelection && experiment.step > 1 && experiment?.data_mappings?.delta_design_table_name) {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    trackEvent('measurement_reupload_warning_shown', { experiment_literal: literal, upload_type: uploadType })
                                    dialog.confirm({
                                        type: 'delete',
                                        title: t('Change Experiment'),
                                        message: t('Are you sure you want to change your Experiment details?'),
                                        warning: t('By Changing this, it will alter the existing experiment details'),
                                        confirmButtonLabel: t('Yes Proceed'),
                                        onConfirm: () => {
                                            trackEvent('measurement_reupload_confirmed', { experiment_literal: literal, upload_type: uploadType })
                                            e.target.click()
                                        },
                                        onCancel: () => {
                                            trackEvent('measurement_reupload_cancelled', { experiment_literal: literal, upload_type: uploadType })
                                            console.log('No')
                                        },
                                    })
                                }

                                if (uploadType == 'measurement' && !recurringSelection && experiment?.data_mappings?.delta_measurement_table_name) {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    trackEvent('measurement_reupload_warning_shown', { experiment_literal: literal, upload_type: uploadType })
                                    if (experiment?.step >= 6 && !experiment?.is_draft) {
                                        measurementHookFunction?.deleteInsightsWarning(() => {
                                            e.target.click()
                                        })
                                    } else {
                                        dialog.confirm({
                                            type: 'delete',
                                            title: t('Change Measurement'),
                                            message: t('Are you sure you want to change your Measurement details?'),
                                            warning: t('By Changing this, it will alter the existing Measurement details'),
                                            confirmButtonLabel: t('Yes Proceed'),
                                            onConfirm: () => {
                                                trackEvent('measurement_reupload_confirmed', { experiment_literal: literal, upload_type: uploadType })
                                                e.target.click()
                                            },
                                            onCancel: () => {
                                                trackEvent('measurement_reupload_cancelled', { experiment_literal: literal, upload_type: uploadType })
                                                console.log('No')
                                            },
                                        })
                                    }
                                }
                            }}
                            className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer"
                            accept=".csv"
                        />
                    </Button>

                    <Button className="!font-medium w-full max-w-[78%] !px-0" theme="secondary" styleType="outline" onClick={downloadTemplate}>
                        {t('Download Template')} <Icon name="download" />
                    </Button>
                </>
            )}

            <UploadSucessModal
                _ref={uploadsucessModalRef}
                uploadfilename={selectedFileNameRef?.current ?? ''}
                filesucess={uploadedDataRef}
                onContinue={continueToStep}
                onReset={() => {
                    if ((uploadType == 'design' && experiment.step > 1) || ((uploadType == 'measurement' || uploadType == 'intervention' || uploadType == 'recommendation') && experiment?.is_completed && !recurringSelection)) {
                        trackEvent('measurement_upload_again_dialog_shown', { experiment_literal: literal, upload_type: uploadType })
                        dialog.confirm({
                            type: 'delete',
                            title: t('Change Experiment'),
                            message: t('Are you sure you want to change your Experiment details?'),
                            warning: t('By Changing this, it will alter the existing experiment details'),
                            confirmButtonLabel: t('Yes Proceed'),
                            onConfirm: () => {
                                trackEvent('measurement_upload_again_confirmed', { experiment_literal: literal, upload_type: uploadType })
                                setWorkingStatus('ready-for-upload')
                            },
                            onCancel: () => {
                                trackEvent('measurement_upload_again_cancelled', { experiment_literal: literal, upload_type: uploadType })
                                //----
                            },
                        })
                    } else {
                        setWorkingStatus('ready-for-upload')
                        selectedFileNameRef.current = null
                    }
                }}
            />

            <RecurringFileUploadErrorModal _ref={recurringFileUploadErrorModalRef} error={recurringFileUploadError} setRecurringFileUploadError={setRecurringFileUploadError} />

            {csvImportMode === 'profile-then-upload' && (
                <CsvImportDialog
                    file={csvImportFile}
                    onCancel={() => {
                        setCsvImportFile(null)
                        const syntheticEvent = { target: { files: [], value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>
                        startUpload(syntheticEvent)
                    }}
                    onConfirm={({ blob, fileName, totalRows, delimiter, configs }) => {
                        console.log('configs ---------------', configs)
                        onCsvImportConfirm?.({ fileName, totalRows, delimiter, configs })
                        const normalizedFile = new File([blob], fileName, { type: blob.type || 'text/csv' })
                        setCsvImportFile(null)
                        const syntheticEvent = { target: { files: [normalizedFile], value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>
                        startUpload(syntheticEvent, configs)
                    }}
                />
            )}
        </div>
    )
}

export default DataFileUploader
